// Security utility for production environments
export class SecurityUtils {
  static isProduction() {
    return process.env.NODE_ENV === 'production' || process.env.VUE_APP_ENVIRONMENT === 'production'
  }

  static sanitizeForLogging(data) {
    if (!this.isProduction()) {
      return data // No sanitization in development
    }

    if (typeof data === 'string') {
      return this.sanitizeString(data)
    }

    if (typeof data === 'object' && data !== null) {
      return this.sanitizeObject(data)
    }

    return data
  }

  static sanitizeString(str) {
    const sensitivePatterns = [
      { pattern: /access_token['":\s]*['"]*([^'",\s}]+)/gi, replacement: 'access_token: [REDACTED]' },
      { pattern: /refresh_token['":\s]*['"]*([^'",\s}]+)/gi, replacement: 'refresh_token: [REDACTED]' },
      { pattern: /bearer\s+([a-zA-Z0-9\-._~+/]+=*)/gi, replacement: 'bearer [REDACTED]' },
      { pattern: /jwt['":\s]*['"]*([^'",\s}]+)/gi, replacement: 'jwt: [REDACTED]' },
      { pattern: /password['":\s]*['"]*([^'",\s}]+)/gi, replacement: 'password: [REDACTED]' },
      { pattern: /secret['":\s]*['"]*([^'",\s}]+)/gi, replacement: 'secret: [REDACTED]' },
      { pattern: /apikey['":\s]*['"]*([^'",\s}]+)/gi, replacement: 'apikey: [REDACTED]' },
      { pattern: /https:\/\/[a-z0-9]+\.supabase\.co/gi, replacement: '[DATABASE_URL_REDACTED]' },
      { pattern: /supabase\.co\/rest\/v1\/[^\s]*/gi, replacement: '[API_ENDPOINT_REDACTED]' },
      // Hide tech stack reveals
      { pattern: /openai_key already configured/gi, replacement: '[TECH_STACK_REDACTED]' },
      { pattern: /gpt_model already configured/gi, replacement: '[TECH_STACK_REDACTED]' },
      { pattern: /azure_token already configured/gi, replacement: '[TECH_STACK_REDACTED]' },
      { pattern: /azure_region already configured/gi, replacement: '[TECH_STACK_REDACTED]' },
      { pattern: /azure_language already configured/gi, replacement: '[TECH_STACK_REDACTED]' },
      { pattern: /Stripe\.js loaded/gi, replacement: 'Payment system loaded' },
      { pattern: /Stripe initialized/gi, replacement: 'Payment system ready' }
    ]

    let sanitized = str
    sensitivePatterns.forEach(({ pattern, replacement }) => {
      sanitized = sanitized.replace(pattern, replacement)
    })

    return sanitized
  }

  static sanitizeObject(obj) {
    const sensitiveKeys = [
      'access_token', 'refresh_token', 'password', 'secret', 'apikey', 'jwt',
      'token', 'auth_token', 'bearer', 'authorization'
    ]

    const sanitized = { ...obj }
    
    Object.keys(sanitized).forEach(key => {
      if (sensitiveKeys.some(sensitiveKey => key.toLowerCase().includes(sensitiveKey))) {
        sanitized[key] = '[REDACTED]'
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeObject(sanitized[key])
      }
    })

    return sanitized
  }

  static secureLog(level, message, data = null) {
    if (this.isProduction()) {
      // In production, only log errors and warnings, and sanitize them
      if (level === 'error' || level === 'warn') {
        const sanitizedMessage = this.sanitizeString(message)
        const sanitizedData = data ? this.sanitizeForLogging(data) : null
        
        if (sanitizedData) {
          console[level](sanitizedMessage, sanitizedData)
        } else {
          console[level](sanitizedMessage)
        }
      }
    } else {
      // In development, log everything normally
      if (data) {
        console[level](message, data)
      } else {
        console[level](message)
      }
    }
  }

  static setupSecureLogging() {
    if (this.isProduction()) {
      // Override console methods in production
      const originalMethods = {
        log: console.log,
        warn: console.warn,
        error: console.error,
        debug: console.debug,
        info: console.info
      }

      // Filter sensitive data from all console methods
      Object.keys(originalMethods).forEach(method => {
        console[method] = (...args) => {
          const sanitizedArgs = args.map(arg => this.sanitizeForLogging(arg))
          originalMethods[method].apply(console, sanitizedArgs)
        }
      })
    }
  }
}

// Initialize secure logging when module loads
SecurityUtils.setupSecureLogging()

export default SecurityUtils
